/**
 * user-recognition-engine.ts
 *
 * Append-Only Contributor Recognition Ledger, Versioned Reputation Records,
 * Independent Appeal Workflow, Anti-Collusion Enforcement, and Badges Engine.
 */

import {
  UserRecognitionProfile,
  RecognitionLedgerEntry,
  ReputationStanding,
  ReputationRecord,
  ReputationAppeal,
  ContributorBadge,
  ContributorBadgeId,
} from './learning-contracts.js';

export class UserRecognitionEngine {
  private readonly ledger: RecognitionLedgerEntry[] = [];
  private readonly profiles = new Map<string, UserRecognitionProfile>();
  private readonly reputationHistory = new Map<string, ReputationRecord[]>();
  private readonly appeals = new Map<string, ReputationAppeal>();
  private readonly maxDailyPoints: number = 100;

  constructor(initialProfiles?: readonly UserRecognitionProfile[]) {
    if (initialProfiles) {
      for (const p of initialProfiles) {
        this.profiles.set(p.principalId, p);
      }
    }
  }

  getProfile(principalId: string, tenantId: string = 'tenant-default'): UserRecognitionProfile {
    let profile = this.profiles.get(principalId);
    if (!profile) {
      const initialRecord: ReputationRecord = {
        recordId: `rep-init-${principalId}`,
        principalId,
        tenantId,
        standing: 'good',
        score: 100,
        confidence: 1.0,
        reasonCodes: ['initial_trust'],
        effectiveAt: new Date().toISOString(),
        decidedBy: 'system',
        revision: 1,
      };

      profile = {
        principalId,
        tenantId,
        totalPoints: 0,
        reputationStanding: 'good',
        currentReputationRecord: initialRecord,
        penaltyCount: 0,
        badgeVisibility: 'organization',
        badges: [],
        verifiedCorrectionsCount: 0,
        candidateImprovementsPromoted: 0,
        peerValidationsCount: 0,
      };
      this.profiles.set(principalId, profile);
      this.reputationHistory.set(principalId, [initialRecord]);
    }
    return profile;
  }

  /**
   * Idempotently award points to a user via an immutable ledger entry.
   * Enforces daily point caps (100 pts/day) and prevents self-review.
   */
  awardPoints(params: {
    principalId: string;
    tenantId?: string;
    sourceEventId: string;
    eventType:
      | 'feedback_validated'
      | 'correction_approved'
      | 'candidate_promoted'
      | 'safety_report_confirmed';
    points: number;
    reason: string;
    reviewerId?: string;
  }): { awarded: number; entry: RecognitionLedgerEntry } {
    const tenantId = params.tenantId || 'tenant-default';

    // Anti-Collusion: Self-Review Check
    if (params.reviewerId && params.reviewerId === params.principalId) {
      throw new Error(`[AntiCollusion] Self-review forbidden: Reviewer ${params.reviewerId} cannot approve own contribution`);
    }

    // Idempotency: Check if already awarded
    const existing = this.ledger.find(
      (e) => e.principalId === params.principalId && e.sourceEventId === params.sourceEventId && e.eventType === params.eventType
    );
    if (existing) {
      return { awarded: existing.points, entry: existing };
    }

    // Calculate today's points for daily cap
    const todayStr = new Date().toISOString().slice(0, 10);
    const todayPoints = this.ledger
      .filter((e) => e.principalId === params.principalId && e.createdAt.startsWith(todayStr) && e.points > 0)
      .reduce((sum, e) => sum + e.points, 0);

    let finalPoints = params.points;
    let eventType: RecognitionLedgerEntry['eventType'] = params.eventType;
    let reason = params.reason;

    if (todayPoints >= this.maxDailyPoints) {
      finalPoints = 0;
      eventType = 'daily_cap_exceeded';
      reason = `${params.reason} (Daily cap of ${this.maxDailyPoints} reached)`;
    } else if (todayPoints + params.points > this.maxDailyPoints) {
      finalPoints = this.maxDailyPoints - todayPoints;
      reason = `${params.reason} (Partial award: daily cap reached)`;
    }

    const entry: RecognitionLedgerEntry = {
      entryId: `ledg-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      principalId: params.principalId,
      tenantId,
      sourceEventId: params.sourceEventId,
      eventType,
      points: finalPoints,
      reason,
      createdAt: new Date().toISOString(),
    };

    this.ledger.push(entry);

    // Update Profile
    const profile = this.getProfile(params.principalId, tenantId);
    let newTotal = profile.totalPoints + finalPoints;
    let verifiedCorrections = profile.verifiedCorrectionsCount;
    let promotedCandidates = profile.candidateImprovementsPromoted;

    if (params.eventType === 'correction_approved') verifiedCorrections++;
    if (params.eventType === 'candidate_promoted') promotedCandidates++;

    const updatedBadges = this.evaluateBadges(profile, newTotal, verifiedCorrections, promotedCandidates, params.eventType);

    const updatedProfile: UserRecognitionProfile = {
      ...profile,
      totalPoints: newTotal,
      verifiedCorrectionsCount: verifiedCorrections,
      candidateImprovementsPromoted: promotedCandidates,
      badges: updatedBadges,
    };

    this.profiles.set(params.principalId, updatedProfile);
    return { awarded: finalPoints, entry };
  }

  /**
   * Applies versioned reputation transition for malicious poisoning, prompt injection, or policy violations.
   * Differentiates innocent mistakes (0 penalty) from malicious actions.
   */
  transitionReputation(params: {
    principalId: string;
    tenantId?: string;
    standing: ReputationStanding;
    penaltyPoints?: number;
    reasonCodes: readonly string[];
    decidedBy: 'system' | 'reviewer' | 'admin';
    decidedByPrincipalId?: string;
    expiresInHours?: number;
  }): ReputationRecord {
    const tenantId = params.tenantId || 'tenant-default';
    const profile = this.getProfile(params.principalId, tenantId);
    const history = this.reputationHistory.get(params.principalId) || [];

    const revision = history.length + 1;
    const now = new Date();
    const expiresAt = params.expiresInHours
      ? new Date(now.getTime() + params.expiresInHours * 3600_000).toISOString()
      : undefined;

    const penalty = Math.abs(params.penaltyPoints || 0);

    const newRecord: ReputationRecord = {
      recordId: `rep-${Date.now()}-${revision}`,
      principalId: params.principalId,
      tenantId,
      standing: params.standing,
      score: Math.max(0, profile.currentReputationRecord.score - penalty),
      confidence: 0.95,
      reasonCodes: params.reasonCodes,
      effectiveAt: now.toISOString(),
      expiresAt,
      decidedBy: params.decidedBy,
      decidedByPrincipalId: params.decidedByPrincipalId,
      revision,
    };

    history.push(newRecord);
    this.reputationHistory.set(params.principalId, history);

    if (penalty > 0) {
      this.ledger.push({
        entryId: `ledg-pen-${Date.now()}`,
        principalId: params.principalId,
        tenantId,
        sourceEventId: newRecord.recordId,
        eventType: 'reputation_penalty_applied',
        points: -penalty,
        reason: `[Reputation Penalty] ${params.reasonCodes.join(', ')}`,
        createdAt: now.toISOString(),
      });
    }

    const updatedProfile: UserRecognitionProfile = {
      ...profile,
      totalPoints: Math.max(0, profile.totalPoints - penalty),
      reputationStanding: params.standing,
      currentReputationRecord: newRecord,
      penaltyCount: penalty > 0 ? profile.penaltyCount + 1 : profile.penaltyCount,
    };

    this.profiles.set(params.principalId, updatedProfile);
    return newRecord;
  }

  /**
   * Helper alias to apply reputation penalty with standing adjustment.
   */
  applyReputationPenalty(params: {
    principalId: string;
    tenantId?: string;
    sourceEventId: string;
    penaltyPoints: number;
    reason: string;
    newStanding?: ReputationStanding;
  }): RecognitionLedgerEntry {
    this.transitionReputation({
      principalId: params.principalId,
      tenantId: params.tenantId,
      standing: params.newStanding || 'probation',
      penaltyPoints: params.penaltyPoints,
      reasonCodes: [params.reason],
      decidedBy: 'reviewer',
    });
    return this.ledger[this.ledger.length - 1]!;
  }

  /**
   * Submits a reputation appeal by a user.
   */
  submitAppeal(principalId: string, explanation: string, tenantId: string = 'tenant-default'): ReputationAppeal {
    const profile = this.getProfile(principalId, tenantId);
    const appealId = `appl-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;

    const appeal: ReputationAppeal = {
      appealId,
      principalId,
      tenantId,
      originalDecisionId: profile.currentReputationRecord.recordId,
      explanation,
      status: 'submitted',
      createdAt: new Date().toISOString(),
    };

    this.appeals.set(appealId, appeal);
    return appeal;
  }

  /**
   * Reviews an appeal with Anti-Collusion: reviewer must not be appellant nor the original decider.
   */
  reviewAppeal(params: {
    appealId: string;
    reviewerId: string;
    verdict: 'accepted' | 'rejected';
    rejectionReason?: string;
  }): ReputationAppeal {
    const appeal = this.appeals.get(params.appealId);
    if (!appeal) throw new Error(`Appeal ${params.appealId} not found`);

    if (appeal.principalId === params.reviewerId) {
      throw new Error(`[AntiCollusion] Appellant ${params.reviewerId} cannot review own appeal`);
    }

    const originalRecord = (this.reputationHistory.get(appeal.principalId) || []).find(
      (r) => r.recordId === appeal.originalDecisionId
    );

    if (originalRecord?.decidedByPrincipalId && originalRecord.decidedByPrincipalId === params.reviewerId) {
      throw new Error(`[AntiCollusion] Original decider ${params.reviewerId} cannot review appeal for own penalty`);
    }

    const updatedAppeal: ReputationAppeal = {
      ...appeal,
      status: params.verdict,
      reviewedBy: params.reviewerId,
      rejectionReason: params.rejectionReason,
      reviewedAt: new Date().toISOString(),
    };

    this.appeals.set(params.appealId, updatedAppeal);

    if (params.verdict === 'accepted') {
      // Revert to good standing
      this.transitionReputation({
        principalId: appeal.principalId,
        tenantId: appeal.tenantId,
        standing: 'good',
        reasonCodes: ['appeal_accepted'],
        decidedBy: 'reviewer',
        decidedByPrincipalId: params.reviewerId,
      });
    }

    return updatedAppeal;
  }

  private evaluateBadges(
    profile: UserRecognitionProfile,
    points: number,
    corrections: number,
    promotions: number,
    lastEventType: string
  ): readonly ContributorBadge[] {
    const badges = [...profile.badges];
    const hasBadge = (id: ContributorBadgeId) => badges.some((b) => b.badgeId === id);

    if (points >= 50 && !hasBadge('top_contributor_bronze')) {
      badges.push({
        badgeId: 'top_contributor_bronze',
        name: 'Bronze Contributor',
        icon: '🥉',
        description: 'Earned 50+ continuous improvement points',
        scope: 'public',
        awardedAt: new Date().toISOString(),
      });
    }

    if (points >= 200 && !hasBadge('top_contributor_silver')) {
      badges.push({
        badgeId: 'top_contributor_silver',
        name: 'Silver Specialist',
        icon: '🥈',
        description: 'Earned 200+ continuous improvement points',
        scope: 'public',
        awardedAt: new Date().toISOString(),
      });
    }

    if (points >= 500 && !hasBadge('top_contributor_gold')) {
      badges.push({
        badgeId: 'top_contributor_gold',
        name: 'Gold Expert',
        icon: '🥇',
        description: 'Earned 500+ points and verified domain contributions',
        scope: 'public',
        awardedAt: new Date().toISOString(),
      });
    }

    if (lastEventType === 'safety_report_confirmed' && !hasBadge('safety_scout')) {
      badges.push({
        badgeId: 'safety_scout',
        name: 'Safety Scout',
        icon: '🛡️',
        description: 'Reported and verified safety or privacy leak anomaly',
        scope: 'organization',
        awardedAt: new Date().toISOString(),
      });
    }

    if (corrections >= 3 && !hasBadge('wiki_curator')) {
      badges.push({
        badgeId: 'wiki_curator',
        name: 'Knowledge Curator',
        icon: '📚',
        description: 'Contributed 3+ verified knowledge corrections',
        scope: 'organization',
        awardedAt: new Date().toISOString(),
      });
    }

    return badges;
  }

  getLedger(): readonly RecognitionLedgerEntry[] {
    return [...this.ledger];
  }

  getReputationHistory(principalId: string): readonly ReputationRecord[] {
    return this.reputationHistory.get(principalId) || [];
  }

  getAppeals(tenantId?: string): readonly ReputationAppeal[] {
    return Array.from(this.appeals.values()).filter((a) => !tenantId || a.tenantId === tenantId);
  }
}
